import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output
} from "@angular/core";
import {PanelService} from "../../../../commons/angular/services/panel.service";
import {AssignAvailabilityDialogComponent} from "../assign-availability-dialog/assign-availability-dialog.component";
import {VisitScheduleResourceModel} from "../../../models/visit-schedule-resource.model";
import {VisitPlanHttpService} from "../../services/visit-plan-http.service";
import {CommonsFacadeService} from "../../../../commons/angular/state/commons/commons-facade.service";
import {takeUntil} from "rxjs/operators";
import {Subject} from "rxjs";
import {VisitPlanModel} from "../../../models/visit-plan.model";
import {EditAvailabilityDialogComponent} from "../edit-availability-dialog/edit-availability-dialog.component";
import {ScheduleType} from "../../../../schedule/types/schedule.type";
import * as moment from 'moment-timezone';
import {VisitScheduleResourceHolderExt} from "../../../models/visit-schedule-resource-holder-ext.model";
import {ResourceSkill} from "../../../../agents/types/resourceSkill.type";
import {VisitPlanRouteModel} from "../../../models/visit-plan-route.model";

interface AvailabilityData {
  available: boolean;
  timeWindow?: {from,to};
  notAvailable?: boolean;
}

require('./availability-table-cell.scss');
@Component({
  standalone: false,
  selector: 'app-availability-table-cell',
  templateUrl: './availability-table-cell.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AvailabilityTableCellComponent implements OnChanges, OnDestroy {

  @Input() availability: AvailabilityData;
  @Input() visitPlan?: VisitPlanModel;
  @Input() visitScheduleId?: string;
  @Input() resources?: VisitScheduleResourceModel[];
  @Input() conflictingResourceIds?: Set<string>;
  @Input() agent?: VisitScheduleResourceHolderExt;
  @Input() date?: string;
  @Input() zoneId?: string;

  @Output() resourcesUpdated = new EventEmitter<VisitScheduleResourceModel[]>();

  activeViewIndex = 0;
  computed: {
    currentProjectResources: VisitScheduleResourceModel[];
    otherProjectResources: VisitScheduleResourceModel[];
    hasOtherProjectAvailability: boolean;
    currentProjectConflictingResources: VisitScheduleResourceModel[];
    hasCurrentProjectConflict: boolean;
    nonConflictingCurrentProjectResources: VisitScheduleResourceModel[];
    hasValidCurrentProjectAvailability: boolean;
    currentProjectConflictTerritories: string;
    isOnAnotherProjectOnly: boolean;
    hasMixedConflictState: boolean;
    carouselResources: VisitScheduleResourceModel[];
    carouselIsConflictPage: boolean;
    carouselTimeWindow: string;
    carouselTerritories: string;
    carouselIsAvailable: boolean;
    conflictingResources: VisitScheduleResourceModel[];
    hasConflict: boolean;
    hasMultipleInSameProject: boolean;
    isFirstResourceAvailable: boolean;
    combinedBranchReferences: any[];
    formattedOtherProjectTimes: string;
    hasLeaveResource: boolean;
    hasNoProjectRecord: boolean;
    formattedOtherProjectTerritories: string;
    displayedTimeWindow: string;
    isCrossDateAvailability: boolean;
    hasTimeWindow: boolean;
    isAvailable: boolean;
    isNotAvailable: boolean;
    isEditDisabled: boolean;
  } = {} as any;

  private destroy$ = new Subject<void>();

  constructor(
    @Inject(PanelService) private panelService: PanelService,
    @Inject(VisitPlanHttpService) private visitPlanHttpService: VisitPlanHttpService,
    @Inject(CommonsFacadeService) private commonsFacadeService: CommonsFacadeService,
    @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef,
    @Inject('splitService') private splitService: any,
    @Inject('schedulingProjectAlertsService') private schedulingProjectAlertsService: any
  ) {
  }

  ngOnChanges() {
    this._computeProperties();
  }

  private _computeProperties() {
    const currentProjectResources = this._getCurrentProjectResources();
    const otherProjectResources = this._getOtherProjectResources();
    const hasLeaveResource = !!(this.resources && this.resources.some(r => r.scheduleType === 'Leave'));
    const hasNoProjectRecord = currentProjectResources.length === 0 && !hasLeaveResource;
    const currentProjectConflictingResources = currentProjectResources.filter(r => this.conflictingResourceIds?.has(r.id));
    const hasCurrentProjectConflict = currentProjectConflictingResources.length > 0;
    const hasOtherProjectAvailability = otherProjectResources.length > 0;
    const nonConflictingCurrentProjectResources = currentProjectResources.filter(r => !this.conflictingResourceIds?.has(r.id));
    const hasValidCurrentProjectAvailability = nonConflictingCurrentProjectResources.length > 0;
    const currentProjectConflictTerritories = this._getCombinedBranches(currentProjectConflictingResources).map(b => b.name).join(', ');
    const hasOtherProjectAvailabilityValue = hasOtherProjectAvailability;
    const isOnAnotherProjectOnly = (hasNoProjectRecord && hasOtherProjectAvailabilityValue) || (hasCurrentProjectConflict && !hasValidCurrentProjectAvailability);
    const hasMixedConflictState = hasCurrentProjectConflict && hasValidCurrentProjectAvailability;
    const carouselResources = this.activeViewIndex === 0 ? nonConflictingCurrentProjectResources : currentProjectConflictingResources;
    const carouselIsConflictPage = this.activeViewIndex === 1;
    const carouselTimeWindow = (() => {
      const withTime = this._getResourcesWithTimeWindow(carouselResources)[0];
      if (!withTime) return '';
      return `${this._formatTime(withTime.startTime)} - ${this._formatTime(withTime.endTime)}`;
    })();
    const carouselTerritories = this._getCombinedBranches(carouselResources).map(b => b.name).join(', ');
    const carouselIsAvailable = (() => {
      const r = carouselResources[0];
      return r ? !r.disabled : false;
    })();
    const conflictingResources = this.resources?.filter(r => this.conflictingResourceIds?.has(r.id)) || [];
    const hasConflict = conflictingResources.length > 0;
    const hasMultipleInSameProject = currentProjectResources.length > 1;
    const isFirstResourceAvailable = (() => {
      if (!hasMultipleInSameProject) {
        return this.availability?.available ?? false;
      }
      const firstProjectResource = currentProjectResources[0];
      return firstProjectResource ? !firstProjectResource.disabled : false;
    })();
    const combinedBranchReferences = this._getCombinedBranches(currentProjectResources);
    const formattedOtherProjectTimes = this._getResourcesWithTimeWindow(otherProjectResources)
      .map(r => `${this._formatTime(r.startTime)} - ${this._formatTime(r.endTime)}`)
      .join(', ');
    const formattedOtherProjectTerritories = (() => {
      const hasUnrestrictedResource = otherProjectResources.some(r => !r.branchIds?.length);
      if (hasUnrestrictedResource) return 'All Territories';
      const branches = this._getCombinedBranches(otherProjectResources);
      if (branches.length === 0) return '';
      return branches.map(b => b.name).join(', ');
    })();
    const displayedTimeWindow = this._computeDisplayedTimeWindow(currentProjectResources);
    const isCrossDateAvailability = this._computeIsCrossDateAvailability(currentProjectResources);
    const hasTimeWindowValue = !!this.availability?.timeWindow;
    const isAvailableValue = this.availability?.available ?? false;
    const isNotAvailableValue = this.availability?.notAvailable ?? false;
    const isEditDisabledValue = this._computeIsEditDisabled(hasLeaveResource, hasMultipleInSameProject, currentProjectResources);

    this.computed = {
      currentProjectResources,
      otherProjectResources,
      hasOtherProjectAvailability: hasOtherProjectAvailabilityValue,
      currentProjectConflictingResources,
      hasCurrentProjectConflict,
      nonConflictingCurrentProjectResources,
      hasValidCurrentProjectAvailability,
      currentProjectConflictTerritories,
      isOnAnotherProjectOnly,
      hasMixedConflictState,
      carouselResources,
      carouselIsConflictPage,
      carouselTimeWindow,
      carouselTerritories,
      carouselIsAvailable,
      conflictingResources,
      hasConflict,
      hasMultipleInSameProject,
      isFirstResourceAvailable,
      combinedBranchReferences,
      formattedOtherProjectTimes,
      hasLeaveResource,
      hasNoProjectRecord,
      formattedOtherProjectTerritories,
      displayedTimeWindow,
      isCrossDateAvailability,
      hasTimeWindow: hasTimeWindowValue,
      isAvailable: isAvailableValue,
      isNotAvailable: isNotAvailableValue,
      isEditDisabled: isEditDisabledValue
    };
    this.cdr.markForCheck();
  }

  private _computeIsEditDisabled(hasLeaveResource: boolean, hasMultipleInSameProject: boolean, currentProjectResources: VisitScheduleResourceModel[]): boolean {
    if (hasLeaveResource) {
      return true;
    }

    if (hasMultipleInSameProject) {
      return false;
    }

    if (currentProjectResources.length === 0) {
      return false;
    }

    if (!this.availability?.timeWindow && !this.availability?.notAvailable) {
      return true;
    }

    return false;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  hasTimeWindow(): boolean {
    return !!this.availability?.timeWindow;
  }

  private _getCurrentProjectResources(): VisitScheduleResourceModel[] {
    if (!this.visitPlan?.branchIds) return this.resources || [];
    return this.resources?.filter(r => !r.branchIds?.length || r.branchIds.some(id => this.visitPlan.branchIds.includes(id))) || [];
  }

  private _getOtherProjectResources(): VisitScheduleResourceModel[] {
    if (!this.visitPlan?.branchIds) return [];
    return this.resources?.filter(r => r.branchIds?.length > 0 && !r.branchIds.some(id => this.visitPlan.branchIds.includes(id))) || [];
  }

  formatResourceTime(r: VisitScheduleResourceModel): string {
    if (r.startTime == null || r.endTime == null) return '';
    return `${this._formatTime(r.startTime)} - ${this._formatTime(r.endTime)}`;
  }

  getResourceBranchesText(r: VisitScheduleResourceModel): string {
    if (!r.branchReferences || r.branchReferences.length === 0) {
      return '';
    }
    return r.branchReferences.map(b => b.name).join(', ');
  }

  private _getCombinedBranches(resources: VisitScheduleResourceModel[]) {
    if (!resources || resources.length === 0) return [];
    const allBranches = [];
    resources.forEach(r => {
      if (r.branchReferences) {
        r.branchReferences.forEach(b => {
          if (!allBranches.some(existing => existing.id === b.id)) {
            allBranches.push(b);
          }
        });
      }
    });
    return allBranches;
  }

  private _getResourcesWithTimeWindow(resources: VisitScheduleResourceModel[]): VisitScheduleResourceModel[] {
    return resources?.filter(r => r.startTime != null && r.endTime != null) || [];
  }

  private _computeDisplayedTimeWindow(currentProjectResources: VisitScheduleResourceModel[]): string {
    const displayedTimedResource = this._getResourcesWithTimeWindow(currentProjectResources)[0];
    if (!displayedTimedResource) return '';
    return `${this._formatTime(displayedTimedResource.startTime)} - ${this._formatTime(displayedTimedResource.endTime)}`;
  }

  private _computeIsCrossDateAvailability(currentProjectResources: VisitScheduleResourceModel[]): boolean {
    const validResources = this._getResourcesWithTimeWindow(currentProjectResources);
    if (validResources.length > 0) {
      return validResources.some(r => this._isCrossDate(r.startTime, r.endTime));
    }

    const timeWindow = this.availability?.timeWindow;
    return !!(timeWindow?.from && timeWindow?.to && this._isCrossDate(timeWindow.from, timeWindow.to));
  }

  private _formatTime(timestamp: number): string {
    const format = this.splitService?.getDateAndTimeConfig()?.time || 'hh:mm A';
    return this._zonedMoment(timestamp).format(format);
  }

  private _zonedMoment(timestamp: number) {
    return this.zoneId ? moment(timestamp).tz(this.zoneId) : moment(timestamp);
  }

  private _isCrossDate(startTime: number, endTime: number): boolean {
    return !this._zonedMoment(startTime).isSame(this._zonedMoment(endTime), 'day');
  }

  onCarouselPrev(event: MouseEvent) {
    event.stopPropagation();
    this.activeViewIndex = this.activeViewIndex === 0 ? 1 : 0;
    this._computeProperties();
  }

  onCarouselNext(event: MouseEvent) {
    event.stopPropagation();
    this.activeViewIndex = this.activeViewIndex === 0 ? 1 : 0;
    this._computeProperties();
  }

  onEditAvailability(event: MouseEvent, forceCreate: boolean = false, resourcesOverride?: VisitScheduleResourceModel[]) {
    const centeredEvent = {target: event.target as HTMLElement};
    const targetResources = resourcesOverride ?? this.computed.currentProjectResources;
    const isCreate = forceCreate || targetResources.length === 0;
    const availabilities = isCreate
      ? [this._buildDefaultProjectAvailabilityDraft()]
      : targetResources;

    const panel = this.panelService.open(
      EditAvailabilityDialogComponent,
      centeredEvent,
      {
        visitPlan: this.visitPlan,
        availabilities,
        date: this.date,
        zoneId: this.zoneId,
        isCreate,
        agent: this.agent
      },
      {
        position: {
          originX: 'center',
          originY: 'center',
          overlayX: 'center',
          overlayY: 'center'
        },
        width: 520,
        height: 'auto'
      }
    );

    if (panel?.afterClose$) {
      panel.afterClose$.subscribe((payload) => {
        const updatedAvailabilities = payload?.availabilities;
        if (updatedAvailabilities && updatedAvailabilities.length) {
          if (isCreate) {
            const resourcesToCreate = updatedAvailabilities.map(updated => ({
              resourceHolderId: this.agent.id,
              startTime: updated.startTime,
              endTime: updated.endTime,
              branchIds: updated.branchIds,
              locationSettings: updated.locationSettings,
              skills: updated.allocationSkills
            }));
            this._createResource(resourcesToCreate, payload?.selectedRoute);
          } else {
            const resourcesToUpdate = updatedAvailabilities.map(updated => ({
              id: updated.id,
              disabled: updated.disabled,
              agentId: this.agent.id,
              startTime: updated.startTime,
              endTime: updated.endTime,
              skills: updated.allocationSkills,
              branchIds: updated.branchIds,
              locationSettings: updated.locationSettings
            }));
            this._updateResource(resourcesToUpdate);
          }
        }
      });
    }

  }

  onAvailableClick(event: MouseEvent) {
    event.stopPropagation();
    if (this.computed.isEditDisabled) {
      return;
    }
    if (this.computed.hasMultipleInSameProject) {
      this.toggleFirstResource(false);
      return;
    }
    if (this.computed.currentProjectResources.length === 0) {
      this.onEditAvailability(event);
      return;
    }
    this.openAssignDialog(event);
  }

  onUnavailableClick(event: MouseEvent) {
    event.stopPropagation();
    if (this.computed.isEditDisabled || this.computed.hasNoProjectRecord) {
      return;
    }
    if (!this.computed.isAvailable && (this.computed.hasTimeWindow || this.computed.isNotAvailable)) {
      return;
    }
    if (this.computed.hasMultipleInSameProject) {
      this.toggleFirstResource(true);
      return;
    }
    this.markAsUnavailable();
  }

  private toggleFirstResource(disabled: boolean) {
    const resource = this.computed.currentProjectResources[0];
    if (!resource) return;

    this._updateResource([{
      id: resource.id,
      disabled,
      agentId: this.agent.holderId,
      startTime: resource.startTime,
      endTime: resource.endTime,
      skills: resource.allocationSkills,
      branchIds: resource.branchIds,
      locationSettings: resource.locationSettings
    }]);
  }

  private openAssignDialog(event: MouseEvent) {
    const centeredEvent = {target: event.target as HTMLElement};

    let fromTime: number | undefined;
    let toTime: number | undefined;
    const projectResources = this.computed.currentProjectResources;

    if (projectResources.length > 0) {
      fromTime = Math.min(...projectResources.map(r => r.startTime));
      toTime = Math.max(...projectResources.map(r => r.endTime));
    }

    const panel = this.panelService.open(
      AssignAvailabilityDialogComponent,
      centeredEvent,
      {
        fromTime,
        toTime,
        date: this.date,
        zoneId: this.zoneId,
        visitPlan: this.visitPlan
      },
      {
        position: {
          originX: 'center',
          originY: 'center',
          overlayX: 'center',
          overlayY: 'center'
        },
        width: 320,
        height: 'auto'
      }
    );

    if (panel?.afterClose$) {
      panel.afterClose$.subscribe((result) => {
        if (result) {
          this.handleDialogResult(result);
        }
      });
    }
  }

  private markAsUnavailable() {
    // Simply call handleDialogResult with unavailable state
    this.handleDialogResult({
      fromTime: null,
      toTime: null
    });
  }

  private handleDialogResult(result: { fromTime: number | null; toTime: number | null }) {
    if (!this.visitPlan || !this.visitScheduleId || !this.date || !this.agent) {
      return;
    }

    const startTime = result.fromTime;
    const endTime = result.toTime;
    const disabled = !result.fromTime || !result.toTime;

    const projectResources = this.computed.currentProjectResources;
    if (projectResources.length === 0) {
      return;
    }

    const resourcesToUpdate = projectResources.map(resource => ({
      id: resource.id,
      skills: resource.allocationSkills,
      branchIds: resource.branchIds,
      disabled,
      agentId: this.agent.id,
      startTime,
      endTime,
      locationSettings: resource.locationSettings
    }));

    this._updateResource(resourcesToUpdate);
  }

  private _getDefaultAllocationSkills(): ResourceSkill[] {
    if (this.agent?.skills?.length) {
      return [...this.agent.skills];
    }
    return [];
  }

  private _buildDefaultProjectAvailabilityDraft(): VisitScheduleResourceModel {
    const m = this.zoneId
      ? moment.tz(this.date, 'YYYY-MM-DD', this.zoneId)
      : moment(this.date, 'YYYY-MM-DD');
    const startTime = m.clone().hour(7).minute(0).second(0).millisecond(0).valueOf();
    const endTime = m.clone().hour(18).minute(30).second(0).millisecond(0).valueOf();
    const branchIds = this.visitPlan?.branchIds?.length ? [...this.visitPlan.branchIds] : [];

    return {
      agentId: this.agent.id,
      visitScheduleId: this.visitScheduleId,
      disabled: false,
      startTime,
      endTime,
      allocationSkills: this._getDefaultAllocationSkills(),
      branchIds,
      branchReferences: [],
      scheduleType: ScheduleType.Availability
    } as VisitScheduleResourceModel;
  }

  private _createResource(resourcesToCreate: Array<{
    resourceHolderId: string;
    startTime: number;
    endTime: number;
    branchIds?: string[];
    locationSettings?: unknown;
    skills?: ResourceSkill[];
  }>, selectedRoute?: VisitPlanRouteModel) {
    this.commonsFacadeService.setLoaderOn();
    this.visitPlanHttpService.createAvailability(this.visitPlan.id, this.visitScheduleId, resourcesToCreate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedResources) => {
          this.commonsFacadeService.setLoaderOff();
          this.resourcesUpdated.emit(updatedResources);
          this.cdr.markForCheck();

          if (selectedRoute && updatedResources?.[0]?.id) {
            this._moveRouteToNewResource(selectedRoute, updatedResources[0].id);
          }
        },
        error: () => {
          this.commonsFacadeService.setLoaderOff();
        }
      });
  }

  private _moveRouteToNewResource(selectedRoute: VisitPlanRouteModel, resourceId: string) {
    this.visitPlanHttpService.moveRoute(this.visitPlan.id, this.visitScheduleId, selectedRoute.id, resourceId, selectedRoute.visitScheduleDayId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (error) => {
          if (error?.status === 412 || error?.error?.code === 412205) {
            this.schedulingProjectAlertsService.reassignRouteDayDispatchedError();
          } else {
            this.schedulingProjectAlertsService.reassignRouteError();
          }
        }
      });
  }

  private _updateResource(resourcesToUpdate) {
    this.commonsFacadeService.setLoaderOn();
    this.visitPlanHttpService.editResources(this.visitPlan.id, this.visitScheduleId, resourcesToUpdate)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (updatedResources) => {
          this.commonsFacadeService.setLoaderOff();
          this.resourcesUpdated.emit(updatedResources);
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.commonsFacadeService.setLoaderOff();
          if (error?.error?.code === 400468) {
            this.schedulingProjectAlertsService.editResourcesSkillRequiredError();
          }
        }
      });
  }

}

